import React, { useState, useEffect, useCallback, useRef } from 'react';
import { COLORS } from '../constants';
import { useLives } from '../context/LivesContext';
import { useNavigate } from 'react-router-dom';

interface Tile {
  value: number;
  id: number;
  mergedFrom?: boolean;
}

interface GameState {
  grid: (Tile | null)[][];
  score: number;
  bestScore: number;
  gameOver: boolean;
  won: boolean;
}

const Game2048: React.FC = () => {
  const { lives, addLives } = useLives();
  const canPlay = lives > 0;
  const [gameState, setGameState] = useState<GameState>({
    grid: Array(4).fill(null).map(() => Array(4).fill(null)),
    score: 0,
    bestScore: 0,
    gameOver: false,
    won: false
  });
  const [allowOneLastGame, setAllowOneLastGame] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  // Touch/swipe state
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const touchEnd = useRef<{ x: number; y: number } | null>(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add a random tile to the grid
  const addRandomTile = useCallback((grid: (Tile | null)[][]) => {
    const availableCells = [];
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        if (!grid[row][col]) {
          availableCells.push({ row, col });
        }
      }
    }
    
    if (availableCells.length > 0) {
      const randomCell = availableCells[Math.floor(Math.random() * availableCells.length)];
      const value = Math.random() < 0.9 ? 2 : 4;
      grid[randomCell.row][randomCell.col] = {
        value,
        id: Math.random()
      };
    }
  }, []);

  // Check if any moves are possible
  const canMove = useCallback((grid: (Tile | null)[][]): boolean => {
    // Check for empty cells
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        if (!grid[row][col]) return true;
      }
    }

    // Check for possible merges
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        const current = grid[row][col];
        if (current) {
          // Check right
          if (col < 3 && grid[row][col + 1] && grid[row][col + 1]!.value === current.value) return true;
          // Check down
          if (row < 3 && grid[row + 1][col] && grid[row + 1][col]!.value === current.value) return true;
        }
      }
    }
    return false;
  }, []);

  // Check if player has won (reached 2048)
  const hasWon = useCallback((grid: (Tile | null)[][]): boolean => {
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        if (grid[row][col] && grid[row][col]!.value >= 2048) return true;
      }
    }
    return false;
  }, []);

  // Initialize game
  const initializeGame = useCallback(() => {
    const newGrid = Array(4).fill(null).map(() => Array(4).fill(null));
    addRandomTile(newGrid);
    addRandomTile(newGrid);
    
    setGameState(prevState => ({
      grid: newGrid,
      score: 0,
      bestScore: prevState.bestScore,
      gameOver: false,
      won: false
    }));
  }, [addRandomTile]);

  // Move tiles in a direction
  const moveTiles = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    setGameState(prevState => {
      const newGrid = prevState.grid.map(row => row.map(tile => tile ? { ...tile } : null));
      let moved = false;
      let score = prevState.score;

      // Helper function to move and merge tiles
      const moveAndMerge = (tiles: (Tile | null)[]) => {
        const filtered = tiles.filter(tile => tile !== null);
        const merged: (Tile | null)[] = [];
        
        for (let i = 0; i < filtered.length; i++) {
          if (i < filtered.length - 1 && filtered[i]!.value === filtered[i + 1]!.value) {
            merged.push({
              value: filtered[i]!.value * 2,
              id: Math.random(),
              mergedFrom: true
            });
            score += filtered[i]!.value * 2;
            i++; // Skip next tile
          } else {
            merged.push(filtered[i]);
          }
        }
        
        // Pad with nulls
        while (merged.length < 4) {
          merged.push(null);
        }
        
        return merged;
      };

      // Move based on direction
      if (direction === 'left' || direction === 'right') {
        // Process each row
        for (let row = 0; row < 4; row++) {
          const rowTiles = [newGrid[row][0], newGrid[row][1], newGrid[row][2], newGrid[row][3]];
          const movedRow = direction === 'left' 
            ? moveAndMerge(rowTiles)
            : moveAndMerge(rowTiles.reverse()).reverse();
          
          for (let col = 0; col < 4; col++) {
            if (newGrid[row][col] !== movedRow[col]) {
              moved = true;
            }
            newGrid[row][col] = movedRow[col];
          }
        }
      } else {
        // Process each column
        for (let col = 0; col < 4; col++) {
          const colTiles = [newGrid[0][col], newGrid[1][col], newGrid[2][col], newGrid[3][col]];
          const movedCol = direction === 'up'
            ? moveAndMerge(colTiles)
            : moveAndMerge(colTiles.reverse()).reverse();
          
          for (let row = 0; row < 4; row++) {
            if (newGrid[row][col] !== movedCol[row]) {
              moved = true;
            }
            newGrid[row][col] = movedCol[row];
          }
        }
      }

      if (moved) {
        addRandomTile(newGrid);
      }

      const bestScore = Math.max(prevState.bestScore, score);
      const gameOver = !canMove(newGrid);
      const won = hasWon(newGrid);

      return {
        ...prevState,
        grid: newGrid,
        score,
        bestScore,
        gameOver,
        won
      };
    });
  }, [addRandomTile, canMove, hasWon]);

  // Touch/swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault(); // Prevent scrolling while swiping
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!canPlay && !allowOneLastGame) return;
    
    touchEnd.current = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    if (!touchStart.current || !touchEnd.current) return;

    const deltaX = touchStart.current.x - touchEnd.current.x;
    const deltaY = touchStart.current.y - touchEnd.current.y;
    const minSwipeDistance = 30;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe
      if (Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          moveTiles('left');
        } else {
          moveTiles('right');
        }
      }
    } else {
      // Vertical swipe
      if (Math.abs(deltaY) > minSwipeDistance) {
        if (deltaY > 0) {
          moveTiles('up');
        } else {
          moveTiles('down');
        }
      }
    }

    touchStart.current = null;
    touchEnd.current = null;
  }, [canPlay, allowOneLastGame, moveTiles]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!canPlay && !allowOneLastGame) return;
      
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          moveTiles('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveTiles('down');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          moveTiles('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          moveTiles('right');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveTiles, canPlay, allowOneLastGame]);

  // Start new game
  const startNewGame = () => {
    if (lives === 1) {
      setAllowOneLastGame(true);
    }
    addLives(-1); // Deduct 1 life
    initializeGame();
  };

  // Initialize game on mount
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  // Get tile background color
  const getTileColor = (value: number): string => {
    const colors: { [key: number]: string } = {
      2: '#eee4da',
      4: '#ede0c8',
      8: '#f2b179',
      16: '#f59563',
      32: '#f67c5f',
      64: '#f65e3b',
      128: '#edcf72',
      256: '#edcc61',
      512: '#edc850',
      1024: '#edc53f',
      2048: '#edc22e'
    };
    return colors[value] || '#3c3a32';
  };

  // Get tile text color
  const getTileTextColor = (value: number): string => {
    return value <= 4 ? '#776e65' : '#f9f6f2';
  };

  // Calculate responsive grid size
  const getGridSize = () => {
    if (isMobile) {
      const screenWidth = window.innerWidth;
      const maxSize = Math.min(screenWidth - 80, 280); // Account for padding
      return maxSize;
    }
    return 320;
  };

  const gridSize = getGridSize();
  const tileSize = (gridSize - 40) / 4; // Account for padding and gaps
  const fontSize = tileSize * 0.4; // Responsive font size

  if (!canPlay && !allowOneLastGame) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        background: '#181818', 
        borderRadius: 12, 
        boxShadow: '0 2px 12px #0004', 
        padding: isMobile ? 16 : 24, 
        maxWidth: isMobile ? '100%' : 440, 
        margin: '0 auto',
        width: isMobile ? 'calc(100vw - 32px)' : 'auto'
      }}>
        <h2 style={{ color: COLORS.WHITE, marginBottom: 12, fontSize: isMobile ? '1.5rem' : '2rem' }}>🎮 2048</h2>
        <div style={{ 
          background: '#333', 
          padding: '2rem', 
          borderRadius: 8, 
          textAlign: 'center',
          color: COLORS.WHITE,
          marginBottom: 16,
          fontSize: isMobile ? '0.9rem' : '1rem'
        }}>
          <p style={{ fontSize: isMobile ? '1rem' : '1.1rem', marginBottom: '1rem' }}>❌ No lives remaining!</p>
          <p style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#aaa' }}>
            Complete quests to earn more lives and play again.
          </p>
        </div>
        <button
          onClick={() => navigate('/games')}
          style={{
            marginTop: 24,
            background: COLORS.PRIMARY,
            color: COLORS.WHITE,
            border: 'none',
            borderRadius: 8,
            padding: '0.8rem 2rem',
            fontSize: '1.1rem',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
            maxWidth: 300
          }}
        >
          Back to Games
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      background: '#181818', 
      borderRadius: 12, 
      boxShadow: '0 2px 12px #0004', 
      padding: isMobile ? 16 : 24, 
      maxWidth: isMobile ? '100%' : 440, 
      margin: '0 auto',
      position: 'relative',
      width: isMobile ? 'calc(100vw - 32px)' : 'auto'
    }}>
      <h2 style={{ color: COLORS.WHITE, marginBottom: 12, fontSize: isMobile ? '1.5rem' : '2rem' }}>🎮 2048</h2>
      
      <div style={{ 
        background: '#333', 
        padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem', 
        borderRadius: 8, 
        marginBottom: 16,
        color: COLORS.WHITE,
        fontSize: isMobile ? '0.8rem' : '0.9rem'
      }}>
        Cost: 1 ❤️ | Lives remaining: {lives}
      </div>

      {/* Score display */}
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? 12 : 16, 
        marginBottom: 16,
        color: COLORS.WHITE 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', color: '#aaa' }}>Score</div>
          <div style={{ fontSize: isMobile ? '1rem' : '1.2rem', fontWeight: 'bold' }}>{gameState.score}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', color: '#aaa' }}>Best</div>
          <div style={{ fontSize: isMobile ? '1rem' : '1.2rem', fontWeight: 'bold' }}>{gameState.bestScore}</div>
        </div>
      </div>

      {/* Game grid */}
      <div style={{ 
        background: '#bbada0', 
        padding: isMobile ? 6 : 8, 
        borderRadius: 8, 
        marginBottom: 16,
        touchAction: 'none' // Prevent default touch behaviors
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      >
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: isMobile ? 6 : 8, 
          width: gridSize, 
          height: gridSize 
        }}>
          {gameState.grid.map((row, x) =>
            row.map((tile, y) => (
              <div
                key={`${x}-${y}`}
                style={{
                  width: tileSize,
                  height: tileSize,
                  background: tile ? getTileColor(tile.value) : '#cdc1b4',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: tile && tile.value >= 100 ? fontSize * 0.75 : fontSize,
                  fontWeight: 'bold',
                  color: tile ? getTileTextColor(tile.value) : 'transparent',
                  transition: 'all 0.15s ease-in-out',
                  userSelect: 'none'
                }}
              >
                {tile ? tile.value : ''}
              </div>
            ))
          )}
        </div>
      </div>


      {/* Game controls */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <button
          onClick={startNewGame}
          style={{
            background: COLORS.PRIMARY,
            color: COLORS.WHITE,
            border: 'none',
            padding: isMobile ? '10px 20px' : '12px 24px',
            borderRadius: 8,
            fontSize: isMobile ? '0.9rem' : '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginRight: 8,
            minHeight: isMobile ? 44 : 48 // Better touch target
          }}
        >
          New Game
        </button>
      </div>

      {/* Instructions */}
      <div style={{ 
        color: '#aaa', 
        fontSize: isMobile ? '0.8rem' : '0.9rem', 
        textAlign: 'center',
        lineHeight: 1.4
      }}>
        {isMobile ? (
          <>
            Swipe to move tiles.<br />
            Combine tiles to reach 2048!
          </>
        ) : (
          <>
            Use arrow keys to move tiles.<br />
            Combine tiles to reach 2048!
          </>
        )}
      </div>

      {/* Game over overlay */}
      {gameState.gameOver && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 12
        }}>
          <div style={{
            background: '#333',
            padding: isMobile ? '1.5rem' : '2rem',
            borderRadius: 8,
            textAlign: 'center',
            color: COLORS.WHITE,
            margin: isMobile ? 16 : 0
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>Game Over!</h3>
            <p style={{ marginBottom: '1rem', fontSize: isMobile ? '0.9rem' : '1rem' }}>Final Score: {gameState.score}</p>
            <button
              onClick={startNewGame}
              style={{
                background: COLORS.PRIMARY,
                color: COLORS.WHITE,
                border: 'none',
                padding: isMobile ? '10px 16px' : '8px 16px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: isMobile ? '0.9rem' : '1rem',
                minHeight: isMobile ? 44 : 36
              }}
            >
              Play Again
            </button>
          </div>
        </div>
      )}

      {/* Win overlay */}
      {gameState.won && !gameState.gameOver && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 12
        }}>
          <div style={{
            background: '#333',
            padding: isMobile ? '1.5rem' : '2rem',
            borderRadius: 8,
            textAlign: 'center',
            color: COLORS.WHITE,
            margin: isMobile ? 16 : 0
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>You Won! 🎉</h3>
            <p style={{ marginBottom: '1rem', fontSize: isMobile ? '0.9rem' : '1rem' }}>Score: {gameState.score}</p>
            <button
              onClick={() => setGameState(prev => ({ ...prev, won: false }))}
              style={{
                background: COLORS.PRIMARY,
                color: COLORS.WHITE,
                border: 'none',
                padding: isMobile ? '10px 16px' : '8px 16px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: isMobile ? '0.9rem' : '1rem',
                minHeight: isMobile ? 44 : 36
              }}
            >
              Continue Playing
            </button>
          </div>
        </div>
      )}
      {/* Back Button */}
      <button
        onClick={() => navigate('/games')}
        style={{
          marginTop: 24,
          background: COLORS.PRIMARY,
          color: COLORS.WHITE,
          border: 'none',
          borderRadius: 8,
          padding: '0.8rem 2rem',
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: 'pointer',
          width: '100%',
          maxWidth: 300
        }}
      >
        Back to Games
      </button>
    </div>
  );
};

export default Game2048; 